import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ActionsMenu from "./ActionsMenu"; // Ensure you have this component
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";

export default function AdmittedBatch() {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [collegeName, setCollegeName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const [mapOpen, setMapOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [regulations, setRegulations] = useState([]);
  const [selectedRegulation, setSelectedRegulation] = useState("");

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;

  const [form, setForm] = useState({
    college_id: "",
    admittedbatch: "",
    status: "ACTIVE",
    created_by: "",
  });

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleOpenMapRegulation = async (row) => {
    setSelectedBatch(row);
    setSelectedRegulation("");
    setMapOpen(true);
    // Fetch all regulations from backend
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/regulations/${clientId}/${collegeId}`);
      if (data.success) setRegulations(data.items);
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load regulations", "error");
    }
  };
  const handleMapSubmit = async () => {
    if (!selectedBatch || !selectedRegulation) {
      showSnackbar("Please select a regulation", "warning");
      return;
    }

    const payload = {
      admittedbatch_id: selectedBatch.admittedbatch_id,
      regulation_id: Number(selectedRegulation),
      created_by: user_email,
    };

    try {
      await axios.post(
        `${API_BASE_URL}/api/admittedbatch-regulations/${clientId}/${collegeId}`,
        payload
      );
      showSnackbar("Regulation mapped successfully", "success");
      await fetchData();
      setMapOpen(false);
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Mapping failed", "error");
    }
  };

  // Actions Menu handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Fetch Data
  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}`
      );
      if (data.success) {
        const mappedRows = data.items.map((r) => ({
          id: r.admittedbatch_id,
          ...r,
          college_name: collegeName || "Unknown",
          created_on: r.created_on
            ? new Date(r.created_on).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "",
        }));
        setAllRows(mappedRows);
        setRows(mappedRows);
        setRowCount(mappedRows.length);
      } else {
        showSnackbar(data.message || "Failed to load data", "error");
      }
    } catch (e) {
      console.error(e);
      showSnackbar(e?.response?.data?.message || "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch College Name
  useEffect(() => {
    if (!collegeId) return;
    axios
      .get(`${API_BASE_URL}/api/colleges/college/${clientId}/${collegeId}`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => {
        if (res.data.success) setCollegeName(res.data.data.college_name);
      })
      .catch((err) => console.error(err));
  }, [collegeId]);

  // Fetch initial data
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (collegeName) fetchData();
  }, [collegeName]);

  // Local search filter
  useEffect(() => {
    if (!search) setRows(allRows);
    else {
      const s = search.toLowerCase();
      setRows(
        allRows.filter(
          (r) =>
            r.admittedbatch?.toLowerCase().includes(s) || r.created_by?.toLowerCase().includes(s)
        )
      );
    }
  }, [search, allRows]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [page, pageSize, search]);

  // Open Add Dialog
  const handleOpenAdd = () => {
    setEditing(null);
    setForm({
      college_id: "",
      admittedbatch: "",
      status: "ACTIVE",
      created_by: "",
    });
    setOpen(true);
  };

  // Open Edit Dialog
  const handleOpenEdit = (row) => {
    setEditing(row);
    setForm({
      college_id: row.college_id,
      admittedbatch: row.admittedbatch,
      status: row.status,
      created_by: row.created_by,
    });
    setOpen(true);
  };

  // Delete Row
  const handleDelete = async (row) => {
    if (!window.confirm("Delete this batch?")) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}/${row.admittedbatch_id}`
      );
      fetchData();
      showSnackbar("Batch deleted successfully", "success");
    } catch (e) {
      console.error(e);
      showSnackbar(e?.response?.data?.message || "Delete failed", "error");
    }
  };

  // Add/Edit Submit
  const handleSubmit = async () => {
    const payload = {
      ...form,
      college_id: Number(collegeId),
      client_id: Number(clientId),
      created_by: user_email,
    };
    try {
      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}/${editing.id}`,
          payload
        );
        showSnackbar("Batch updated successfully", "success");
      } else {
        await axios.post(`${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}`, payload);
        showSnackbar("Batch created successfully", "success");
      }
      setOpen(false);
      fetchData();
    } catch (e) {
      console.error(e);
      showSnackbar(e?.response?.data?.message || "Save failed", "error");
    }
  };

  // CSV Upload
  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}/upload/csv`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      showSnackbar(data.message || "CSV uploaded successfully", "success");
      fetchData();
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Upload failed", "error");
    } finally {
      e.target.value = "";
    }
  };

  // Capitalize first letter
  function initCap(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  const columns = useMemo(
    () => [
      { field: "admittedbatch_id", headerName: "ID", flex: 0.5, minWidth: 50 },
      { field: "college_name", headerName: "College Name", flex: 2, minWidth: 150 },
      { field: "admittedbatch", headerName: "Batch", flex: 1, minWidth: 100 },
      {
        field: "regulation_code",
        headerName: "Regulation Code",
        flex: 1,
        minWidth: 120,
        valueGetter: (p) => p.row.regulation_code || "Not Mapped",
      },
      {
        field: "created_on",
        headerName: "Created On",
        flex: 1.2,
        minWidth: 120,
        valueGetter: (p) => p.row.created_on?.slice(0, 10),
      },
      { field: "created_by", headerName: "Created By", flex: 1, minWidth: 120 },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => (
          <span style={{ color: params.value === "ACTIVE" ? "green" : "red" }}>{params.value}</span>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <ActionsMenu
            row={params.row}
            onEdit={handleOpenEdit}
            onDelete={handleDelete}
            onMapRegulation={handleOpenMapRegulation}
          />
        ),
      },
    ],
    []
  );

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            ADMITTED BATCH
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={handleMenuOpen}
          >
            Actions
          </Button>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleOpenAdd();
                }}
              >
                ADD BATCH
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#357a38" },
                }}
                onClick={() => {
                  handleMenuClose();
                  const link = document.createElement("a");
                  link.href = `${process.env.PUBLIC_URL}/admittedbatch.csv`;
                  link.setAttribute("download", "admittedbatch.csv");
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  backgroundColor: "#ff9800",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c77700" },
                }}
              >
                UPLOAD CSV
                <input
                  hidden
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    handleMenuClose();
                    handleUploadCsv(e);
                  }}
                />
              </Button>
            </MenuItem>
          </Menu>
          <TextField
            size="medium"
            placeholder="Search data"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => fetchData()}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            pageSize={pageSize} // controlled pageSize
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            pagination
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      {/* Dialog for Add/Edit */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Batch" : "Add Batch"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Batch (e.g., 2025-2029)"
                value={form.admittedbatch}
                onChange={(e) => setForm({ ...form, admittedbatch: e.target.value.toUpperCase() })}
                required
                fullWidth
                inputProps={{ maxLength: 10 }}
              />
              <TextField
                label="Status"
                margin="dense"
                required
                select
                fullWidth
                value={form.status}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="" disabled hidden>
                  Select Status
                </option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              ml: 2,
              backgroundColor: "#ff9800",
              color: "#fff",
              "&:hover": { backgroundColor: "#c77700" },
            }}
          >
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={mapOpen} onClose={() => setMapOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Map Regulation to Batch</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography>
              Mapping for Batch: <b>{selectedBatch?.admittedbatch}</b>
            </Typography>
            <TextField
              label="Select Regulation"
              margin="dense"
              required
              select
              fullWidth
              value={selectedRegulation}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setSelectedRegulation(e.target.value)}
              SelectProps={{ native: true }}
            >
              {regulations.map((reg) => (
                <option key={reg.regulation_id} value={reg.regulation_id}>
                  {reg.regulation_code} - {reg.regulation_name}
                </option>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleMapSubmit}
            sx={{
              ml: 2,
              backgroundColor: "#4caf50",
              color: "#fff",
              "&:hover": { backgroundColor: "#357a38" },
            }}
          >
            Map Regulation
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
